import { createFileRoute } from "@tanstack/react-router";
import { useContext, useState } from "react";
import { parseDate, type CalendarDate } from "@internationalized/date";
import { endOfDay, format, startOfDay } from "date-fns";
import {
  AppError,
  Avatar,
  Badge,
  Button,
  DatePicker,
  EmptyState,
  Loader,
  PopoverMenu,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  type TagTone,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { AuthContext, AuthUtils, isDefined } from "@bfc-software/app-toolbox";
import { AuditServiceSchemaTypes } from "@bfc-software/api-schemas";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useAuditApi } from "@bfc-software/bfc-web-ui/lib/audit-api-client";
import {
  type PageSearch,
  validatePageSearch,
} from "@bfc-software/bfc-web-ui/lib/cursor-pagination";

import { ActionTabs, type ActionCategory } from "./-components/action-tabs";
import { AuditPager } from "./-components/audit-pager";
import styles from "./index.module.css";

type AuditEvent = AuditServiceSchemaTypes.components["schemas"]["AuditEvent"];

/**
 * The service's own default/max (audit_service.yaml) -- 200 in one page keeps
 * the pager relevant without a second round trip for every warehouse's
 * ordinary day. Everything below (the action tabs, the search box) filters
 * this one loaded page client-side; only the date range and the cursor are
 * real server params.
 */
const PAGE_SIZE = 200;

const COLUMN_COUNT = 7;

/**
 * A mapped event's rendered record (render.RenderedEvent on the service).
 * `AuditEvent.rendered` is untyped on the wire -- an event_type with no
 * mapping file yet comes back as the raw stored payload instead (ADR §3.4),
 * so this has to be checked at read time rather than trusted from the schema.
 */
type RenderedEvent = {
  Action: string;
  Entity: string;
  EntityName: string;
  Target: string;
  Before: string;
  After: string;
  Administrator: string;
  Timestamp: string;
};

const RENDERED_EVENT_KEYS: ReadonlyArray<keyof RenderedEvent> = [
  "Action",
  "Entity",
  "EntityName",
  "Target",
  "Before",
  "After",
  "Administrator",
  "Timestamp",
];

function asRenderedEvent(value: unknown): RenderedEvent | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const isRendered = RENDERED_EVENT_KEYS.every(
    (key) => typeof record[key] === "string",
  );
  return isRendered ? (record as unknown as RenderedEvent) : null;
}

/**
 * Buckets a rendered event's free-text `Action` verb (Create, Update, Delete,
 * Import, Add, Remove, Rename, Reset, Activate, Deactivate -- see render.go's
 * RenderedEvent doc comment) into the future-state design's three tabs. An
 * event with no mapping yet (rendered === null) has no verb to bucket and
 * only ever counts under "All".
 */
type ActionBucket = Exclude<ActionCategory, "all">;

const CREATION_VERBS = new Set(["Create", "Import", "Add"]);
const DEACTIVATION_VERBS = new Set(["Delete", "Remove", "Deactivate"]);

function actionBucket(rendered: RenderedEvent | null): ActionBucket | null {
  if (!rendered) return null;
  if (CREATION_VERBS.has(rendered.Action)) return "creations";
  if (DEACTIVATION_VERBS.has(rendered.Action)) return "deactivations";
  return "modifications";
}

const BUCKET_TONES: Record<ActionBucket, TagTone> = {
  creations: "success",
  modifications: "info",
  deactivations: "danger",
};

/** yyyy-mm-dd, the shape `CalendarDate#toString()` produces and `parseDate` reads back. */
function isDateString(raw: unknown): raw is string {
  if (typeof raw !== "string" || raw.length === 0) return false;
  try {
    parseDate(raw);
    return true;
  } catch {
    return false;
  }
}

function calendarDateToJsDate(value: CalendarDate): Date {
  return new Date(value.year, value.month - 1, value.day);
}

/** The `from`/`to` query params are date-times; the filter is day-granularity. */
function dayStartIso(dateString: string): string {
  return startOfDay(calendarDateToJsDate(parseDate(dateString))).toISOString();
}

function dayEndIso(dateString: string): string {
  return endOfDay(calendarDateToJsDate(parseDate(dateString))).toISOString();
}

function isActionCategory(raw: unknown): raw is ActionCategory {
  return (
    raw === "all" ||
    raw === "creations" ||
    raw === "modifications" ||
    raw === "deactivations"
  );
}

function nonEmptyString(raw: unknown): string | undefined {
  return typeof raw === "string" && raw.length > 0 ? raw : undefined;
}

type AuditLogSearch = {
  q?: string;
  category?: ActionCategory;
  from?: string;
  to?: string;
} & PageSearch;

/**
 * The service's `after`/`before` params, not `cursor-pagination.ts`'s own
 * `cursor`/`before` names -- the URL still only ever carries one `cursor` +
 * `dir` pair (see that module's doc comment), this just maps it onto this
 * one endpoint's own query param spelling.
 */
function auditPageQuery(page: PageSearch): { after?: string; before?: string } {
  if (typeof page.cursor === "undefined") return {};
  return page.dir === "prev" ? { before: page.cursor } : { after: page.cursor };
}

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/audit-log/",
)({
  validateSearch: (search?: Record<string, unknown>): AuditLogSearch => {
    const category = isActionCategory(search?.["category"])
      ? search["category"]
      : undefined;
    const from = isDateString(search?.["from"]) ? search["from"] : undefined;
    const to = isDateString(search?.["to"]) ? search["to"] : undefined;
    const q = nonEmptyString(search?.["q"]);

    return {
      ...(q ? { q } : {}),
      ...(category && category !== "all" ? { category } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...validatePageSearch(search),
    };
  },
  component: RouteComponent,
});

function TimestampCell({ value }: { value: string }) {
  const date = new Date(value);
  return (
    <div className={styles.timestamp}>
      <div>{format(date, "PP")}</div>
      <div className={styles.timestampTime}>{format(date, "p")}</div>
    </div>
  );
}

function AuditEventRow({
  event,
  rendered,
  bucket,
  isExpanded,
  onToggle,
  t,
}: {
  event: AuditEvent;
  rendered: RenderedEvent | null;
  bucket: ActionBucket | null;
  isExpanded: boolean;
  onToggle: () => void;
  t: TFunction;
}) {
  const administrator = rendered
    ? rendered.Administrator
    : (event.actor.user_name ??
      t(`auditLog.actorKind.${event.actor.actor_kind}`));

  return (
    <>
      <TableRow data-test-role="audit-event-row">
        <TableCell>
          <Tag
            label={rendered ? rendered.Action : t("auditLog.table.unmapped")}
            display={{ tone: bucket ? BUCKET_TONES[bucket] : "neutral" }}
          />
        </TableCell>
        <TableCell>
          <div className={styles.entityCell}>
            <span className={styles.entityType}>
              {rendered ? rendered.Entity : event.event_type}
            </span>
            {rendered?.EntityName ? (
              <span className={styles.entityName}>{rendered.EntityName}</span>
            ) : null}
          </div>
        </TableCell>
        <TableCell>
          {rendered ? (
            rendered.Target
          ) : (
            <Button
              kind="text-only"
              variant="secondary"
              scale="inline"
              text={isExpanded ? t("auditLog.collapse") : t("auditLog.expand")}
              onPress={onToggle}
              data-test-role="audit-event-toggle"
            />
          )}
        </TableCell>
        <TableCell>
          {rendered && rendered.Before !== "none" && rendered.Before !== "" ? (
            <Tag label={rendered.Before} display={{ tone: "danger" }} />
          ) : (
            <span className="text-on-surface-variant">—</span>
          )}
        </TableCell>
        <TableCell>
          {rendered && rendered.After !== "none" && rendered.After !== "" ? (
            <Tag label={rendered.After} display={{ tone: "success" }} />
          ) : (
            <span className="text-on-surface-variant">—</span>
          )}
        </TableCell>
        <TableCell>
          <div className={styles.administratorCell}>
            <Avatar name={administrator} colorKey={administrator} size="sm" />
            <span>{administrator}</span>
          </div>
        </TableCell>
        <TableCell>
          <TimestampCell value={event.received_at} />
        </TableCell>
      </TableRow>
      {isExpanded && !rendered && (
        <TableRow data-test-role="audit-event-expanded">
          <TableCell colSpan={COLUMN_COUNT}>
            <pre className={styles.rawPayload}>
              {JSON.stringify(event.rendered, null, 2)}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function RouteComponent() {
  const { lang, org, location } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useAuditApi();

  // eslint-disable-next-line no-restricted-properties -- TODO: migrate to useAssertedTenancyContext()
  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const enabled = typeof orgId === "string";

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const updateFilters = (patch: Partial<AuditLogSearch>) => {
    navigate({
      search: (prev) => {
        const merged: AuditLogSearch = { ...prev, ...patch };
        delete merged.cursor;
        delete merged.dir;
        return merged;
      },
      resetScroll: false,
    });
  };

  const query = client.useQuery(
    "get",
    "/v1/{orgExtId}/warehouses/{warehouseExtId}/audit-events",
    {
      params: {
        path: { orgExtId: orgId!, warehouseExtId: location },
        query: {
          limit: PAGE_SIZE,
          ...(search.from ? { from: dayStartIso(search.from) } : {}),
          ...(search.to ? { to: dayEndIso(search.to) } : {}),
          ...auditPageQuery(search),
        },
      },
    },
    { enabled },
  );

  const withBucket = (query.data?.items ?? []).map((event) => {
    const rendered = asRenderedEvent(event.rendered);
    return { event, rendered, bucket: actionBucket(rendered) };
  });

  const counts: Record<ActionCategory, number> = {
    all: withBucket.length,
    creations: withBucket.filter((row) => row.bucket === "creations").length,
    modifications: withBucket.filter((row) => row.bucket === "modifications")
      .length,
    deactivations: withBucket.filter((row) => row.bucket === "deactivations")
      .length,
  };

  const category = search.category ?? "all";
  const byCategory =
    category === "all"
      ? withBucket
      : withBucket.filter((row) => row.bucket === category);

  const searchText = search.q?.trim().toLowerCase();
  const rows = searchText
    ? byCategory.filter(({ event, rendered }) => {
        const haystack = rendered
          ? [
              rendered.Administrator,
              rendered.Entity,
              rendered.EntityName,
              rendered.Target,
            ]
          : [event.event_type, event.actor.user_name ?? ""];
        return haystack.some((value) =>
          value.toLowerCase().includes(searchText),
        );
      })
    : byCategory;

  const hasDateFilter = isDefined(search.from) || isDefined(search.to);

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <div className={styles.headingRow}>
              <h1 className="page-title">{t("auditLog.heading")}</h1>
              <Badge
                label={t("auditLog.badge")}
                color={{ mode: "static", tone: "purple" }}
              />
            </div>
            <p className="page-subtitle">{t("auditLog.subheading")}</p>
          </div>
        </header>

        <div className={styles.filterBar}>
          <ActionTabs
            counts={counts}
            selected={category}
            onChange={(next) =>
              updateFilters({ category: next === "all" ? undefined : next })
            }
            t={t}
          />
          <div className={styles.searchField}>
            <Search
              aria-label={t("auditLog.search.ariaLabel")}
              placeholder={t("auditLog.search.placeholder")}
              value={search.q ?? ""}
              onChange={(value) => updateFilters({ q: String(value) })}
              onClear={() => updateFilters({ q: undefined })}
            />
          </div>
          <PopoverMenu
            trigger={{
              kind: "icon-only",
              variant: hasDateFilter ? "filled" : "tertiary",
              icon: "Calendar",
              label: t("auditLog.filters.dateRange"),
            }}
          >
            <div className={styles.datePopover}>
              <DatePicker
                label={t("auditLog.filters.fromLabel")}
                value={search.from ? parseDate(search.from) : undefined}
                maxValue={search.to ? parseDate(search.to) : undefined}
                onChange={(value) =>
                  updateFilters({ from: value ? value.toString() : undefined })
                }
              />
              <DatePicker
                label={t("auditLog.filters.toLabel")}
                value={search.to ? parseDate(search.to) : undefined}
                minValue={search.from ? parseDate(search.from) : undefined}
                onChange={(value) =>
                  updateFilters({ to: value ? value.toString() : undefined })
                }
              />
              <Button
                kind="link"
                variant="accent"
                text={t("auditLog.filters.clearDates")}
                isDisabled={!hasDateFilter}
                onPress={() =>
                  updateFilters({ from: undefined, to: undefined })
                }
              />
            </div>
          </PopoverMenu>
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("auditLog.refresh")}
            isDisabled={query.isFetching}
            onPress={() => query.refetch()}
            data-test-role="audit-log-refresh"
          />
        </div>

        <div className="table-frame">
          {query.isLoading || !enabled ? (
            <Loader type="page" />
          ) : query.isError ? (
            <AppError
              description={t("auditLog.failed")}
              resolution={t("auditLog.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("auditLog.table.ariaLabel")}
              className={rows.length < 1 ? "min-h-full" : ""}
              isResizable={false}
            >
              <TableHead>
                <TableHeaderCell isRowHeader={true}>
                  {t("auditLog.table.action")}
                </TableHeaderCell>
                <TableHeaderCell>{t("auditLog.table.entity")}</TableHeaderCell>
                <TableHeaderCell>{t("auditLog.table.field")}</TableHeaderCell>
                <TableHeaderCell>{t("auditLog.table.before")}</TableHeaderCell>
                <TableHeaderCell>{t("auditLog.table.after")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("auditLog.table.administrator")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("auditLog.table.timestamp")}
                </TableHeaderCell>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <EmptyState heading={t("auditLog.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(({ event, rendered, bucket }) => (
                    <AuditEventRow
                      key={event.id}
                      event={event}
                      rendered={rendered}
                      bucket={bucket}
                      isExpanded={expanded.has(event.id)}
                      onToggle={() => toggle(event.id)}
                      t={t}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        <AuditPager
          pagination={query.data}
          isFetching={query.isFetching}
          onChange={(next) =>
            navigate({
              search: (prev) => ({
                ...prev,
                cursor: next.cursor,
                dir: next.dir,
              }),
            })
          }
          t={t}
        />
      </div>
    </div>
  );
}
